import { useCustom, type HttpError } from "@refinedev/core";
import { useMemo } from "react";
import type { ListDefinition, ListValue } from "../types";

interface UseListValuesOptions {
  listNames: string[];
  enabled?: boolean;
}

interface UseListValuesResult {
  lists: Record<string, ListValue[]>;
  isLoading: boolean;
  isError: boolean;
  error: HttpError | null;
}

/**
 * Hook to fetch list values for multiple lists from the API.
 * Used for populating dropdown options in forms.
 *
 * @param listNames - Array of list names to fetch (e.g., ["Lead Source", "Lead Priority"])
 * @param enabled - Whether to enable the fetch
 */
export const useListValues = ({
  listNames,
  enabled = true,
}: UseListValuesOptions): UseListValuesResult => {
  // Only fetch if we have list names and are enabled
  const shouldFetch = enabled && listNames.length > 0;

  // Fetch all lists endpoint (contains all list definitions)
  const {
    data: listsData,
    isLoading,
    isError,
    error,
  } = useCustom<ListDefinition[]>({
    url: "/meta/lists",
    method: "get",
    queryOptions: {
      enabled: shouldFetch,
      staleTime: 30 * 60 * 1000, // 30 minutes cache
    },
  });

  // Extract and filter lists we need
  const lists = useMemo(() => {
    const result: Record<string, ListValue[]> = {};

    if (!listsData?.data) {
      return result;
    }

    const allLists = listsData.data as ListDefinition[];

    // Find lists by name and extract their values
    for (const listName of listNames) {
      const listDef = allLists.find(
        (l) =>
          l.name === listName ||
          l.name.toLowerCase() === listName.toLowerCase(),
      );

      if (listDef) {
        // Sort by sortOrder and return values
        result[listName] = [...listDef.values].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
      } else {
        // List not found, return empty array
        result[listName] = [];
      }
    }

    return result;
  }, [listsData?.data, listNames]);

  return {
    lists,
    // Only report loading if we're actually fetching
    isLoading: shouldFetch && isLoading,
    isError,
    error: error || null,
  };
};
